import React, {
    FC,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import Button from "@mui/material/Button";

import {
    FormCardInput,
    FormChipsInput,
    FormDateInput,
    FormDateRangeInput,
    FormDateTimeInput,
    FormDropdownInput,
    FormField,
    FormFieldTextAddressInput,
    FormStep,
    FormStepIFrame,
    FormSteps,
    FormTextInput,
    SelectableItem,
} from "stentor-models";

import { addContextPrefix, shallowEqualBooleanMap } from "../../utils/comparator";
import {
    isFormCheckInput,
    isFormChipsInput,
    isFormDateInput,
    isFormDateRangeInput,
    isFormDateTimeInput,
    isFormTextInput,
    isFormDropdownInput,
} from "../../utils/guards";
import { joinWithAnd, reformatDate, replaceValues } from "../../utils/templating";
import {
    DEFAULT_DATE_FIELD_NAME,
    DEFAULT_DATE_TIME_FIELD_NAME,
} from "../../utils/timeSlots";
import { SearchWidgetConfigContext } from "../../utils/SearchWidgetConfigContext";
import { useSearchState } from "../../store";
import { useExternalWidget, ExternalWidgetConfig } from "../../utils/useExternalWidget";

import FormFieldSelect from "../FormFieldChips/FormFieldSelect";
import { FormFieldCard } from "../FormFieldCard";
import { FormFieldChips } from "../FormFieldChips";
import { FormFieldTextInput } from "../FormFieldTextInput";
import { FormFieldDropdown } from "../FormFieldDropdown";
import { FormFieldDate } from "../FormFieldDate";
import { FormDateRangeValue, FormFieldDateRange } from "../FormFieldDateRange";
import { FormDateTimeValue, FormFieldDateTime } from "../FormFieldDateTime";
import { FormFieldTextInputAddress } from "../FormFieldTextInputAddress";
import { SpinnerIcon } from "../icons";

import {
    FieldValidatorResult,
    fieldErrorsEqual,
    formatUSPhoneNumber,
    formatZipCode,
    isEmpty,
    isEmptyString,
    validateEmail,
    validatePhoneNumber,
    validateZipCode,
} from "./validators";

import styles from "./FormStepDisplay.module.scss";
// Global (non-module) overrides that restyle the CostGuide partner embed rendered under
// #airo-anchor. Kept separate from the CSS-module styles above because it targets the
// partner's own light-DOM class names, which must not be scoped. See costguide-overrides.scss.
import "./costguide-overrides.scss";
import { isSelectableItem } from "../FormFieldChips/FormFieldChips";

/**
 * Extended FormStep interface for per-step properties not yet in stentor-models.
 * Until they land upstream (targeting stentor-models@1.75.0), we use this type
 * extension to maintain type safety.
 */
interface FormStepWithUnloadWarning extends FormStep {
    warnBeforeUnload?: boolean;
    warnBeforeUnloadMessage?: string;
    /**
     * Suppresses the horizontal gutters (ModalDialog + step padding) for this
     * step only, so a full-width embed (e.g. an external booking widget) can
     * render edge-to-edge. From stentor-models@1.75.0's FormStep.fullBleed.
     */
    fullBleed?: boolean;
}

/**
 * Local extension of FormStep for the external booking-widget handoff step,
 * mirroring stentor-models@1.75.0's FormStepExternalWidget. See the note on
 * FormStepWithUnloadWarning above for why this is redeclared locally.
 */
export interface FormStepExternalWidget extends FormStep {
    externalWidget: ExternalWidgetConfig;
}

export function isFormStepIframe(step: FormSteps): step is FormStepIFrame {
    return !!step && (step as FormStepIFrame).iframe !== undefined;
}

export function isFormStepExternalWidget(step: FormSteps): step is FormStepExternalWidget {
    return !!step && (step as FormStepExternalWidget).externalWidget !== undefined;
}

type FormFieldWithValue = FormField & { value: unknown };
function hasFieldValue(f: FormField): f is FormFieldWithValue {
    return "value" in f && (f as FormFieldWithValue).value !== undefined;
}

export interface StepData {
    current?: { [key: string]: unknown };
}

type Groupable<T extends FormField> = T & { mandatoryGroup?: string };

interface GroupableFieldStep extends FormStep {
    fields: Groupable<FormField>[];
}

function isGroupableFieldField(step: FormField): step is Groupable<FormField> {
    return !!step && (step as Groupable<FormField>).mandatoryGroup !== undefined;
}

export interface FormStepDisplayProps {
    /**
     * Step data
     */
    readonly formStep: FormSteps | GroupableFieldStep;
    /**
     * When the user hits the next button.
     *
     * If submit is true, then the form should be submitted.
     * May return a Promise when a FORM_NEXT server callback is in flight.
     */
    onNextStep: ((submit?: boolean) => Promise<void> | void) | undefined;
    /**
     * When the user hits the previous button
     */
    onPrevStep: (() => void) | undefined;
    /**
     * Data from the step
     */
    onData: (data: StepData) => void;
    /**
     * Data from the step but split up into key, value pairs.
     *
     * For example, {"name": "John Smith"}
     */
    onDataWithLabels: (data: object) => void;

    getData: () => object;
    /**
     * Returns the data needed for the form
     *
     * @returns
     */
    getDataWithLabels: () => object;
    /**
     * Is the form loading
     */
    loading: boolean;
    /**
     * Current step index (0-indexed)
     */
    currentStepIndex?: number;
    /**
     * Total number of steps in the form
     */
    totalSteps?: number;
    /**
     * Callback to track next_step event
     */
    onTrackNextStep?: (
        stepName: string,
        stepIndex: number,
        stepData: Record<string, unknown>,
        isLastStep: boolean
    ) => void;
    /**
     * Optional ref to sync current step data for tracking purposes.
     * This allows parent components to access live form data before step completion.
     */
    currentStepDataRef?: React.MutableRefObject<Record<string, unknown>>;
    /**
     * Whether this step is the one currently visible. Every step's FormStepDisplay
     * is mounted at once (FormDisplay toggles visibility via CSS), so this is what
     * gates the external-widget mount sequence to only run once the step actually
     * becomes active, rather than for every handoff step on the form's first render.
     */
    isActive?: boolean;
    /**
     * Fired when a booking-widget handoff step's anchor gains content within
     * its render timeout.
     */
    onTrackHandoffRendered?: (stepName: string, provider: string) => void;
    /**
     * Fired when a booking-widget handoff step's render timeout elapses with
     * an empty anchor.
     */
    onTrackHandoffNoRender?: (stepName: string, provider: string) => void;
    /**
     * Fired when the partner script invokes the configured success callback.
     */
    onTrackHandoffScheduled?: (stepName: string, provider: string, payload?: unknown) => void;
    /**
     * Fired when a booking-widget handoff step's script fails to load or is rejected.
     */
    onTrackHandoffError?: (stepName: string, provider: string) => void;
    /**
     * Fired when a booking-widget handoff step fails to render (timeout or script
     * error) and `externalWidget.fallbackStep` is set, so the parent can navigate
     * there. Not fired when fallbackStep is unset -- an inline fallback message is
     * rendered in place instead.
     */
    onExternalWidgetFallback?: (fallbackStep: string) => void;
    /**
     * Message shown when this step's FORM_SUBMIT dispatch failed, so the user knows
     * their submission did not land and can retry rather than assuming it worked
     * (issue #1373). Undefined when the step has no failed submit.
     */
    submitError?: string;
    /**
     * Suppresses this step's own title/subtitle block. Set when a partner frame is
     * around the step, since the frame's intro block renders them instead -- and
     * placement is the frame's decision (it can sit outside the frame entirely).
     */
    hideIntro?: boolean;
}

/**
 * Both halves present. `FormFieldDateRange` reports `undefined` when both are empty and a
 * partial object when only one is filled, so this distinguishes "untouched" from "half done"
 * without either being treated as a usable range.
 */
/**
 * A datetime field holds two values and is only satisfied by both. `start` rather than
 * the label, because an offering without a label still formats one for display.
 */
function isCompleteDateTime(value: unknown): boolean {
    const selection = value as FormDateTimeValue | undefined;

    return !!selection?.date && !!selection?.start;
}

function isCompleteRange(value: unknown): boolean {
    const range = value as { from?: string; to?: string } | undefined;
    return !!range?.from && !!range?.to;
}

export const FormStepDisplay: FC<FormStepDisplayProps> = (props) => {
    const stepData = useRef<StepData>({});
    // Track which fields were prefilled from server values (not user input)
    const prefilledFields = useRef<Set<string>>(new Set());
    const hasNotifiedPrefill = useRef(false);
    const prevFieldsRef = useRef(props.formStep.fields);

    const fieldTests = useRef({});

    const [mandatoryGroups, setMandatoryGroups] = useState<{ [name: string]: string[] }>({});
    const [fieldConditions, setFieldConditions] = useState<{ [name: string]: boolean }>({});
    const [fieldErrors, setFieldErrors] = useState<{ [name: string]: FieldValidatorResult }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const {
        formStep,
        onNextStep: nextStep,
        onPrevStep: prevStep,
        onData: handleData,
        onDataWithLabels: handleDataWithLabels,
        getData,
        getDataWithLabels,
        loading,
        currentStepIndex,
        totalSteps,
        onTrackNextStep,
        currentStepDataRef,
        isActive,
        onTrackHandoffRendered,
        onTrackHandoffNoRender,
        onTrackHandoffScheduled,
        onTrackHandoffError,
        onExternalWidgetFallback,
        submitError,
        hideIntro,
    } = props;

    // Gated on isActive: every step's FormStepDisplay is mounted at once, so
    // without this the mount sequence would fire for a handoff step as soon as
    // the form first renders, rather than when the step actually becomes active.
    const externalWidgetConfig =
        isActive && isFormStepExternalWidget(formStep) ? formStep.externalWidget : undefined;

    const { outcome: externalWidgetOutcome } = useExternalWidget(externalWidgetConfig, {
        onRendered: (provider) => onTrackHandoffRendered?.(formStep.name, provider),
        onNoRender: (provider) => onTrackHandoffNoRender?.(formStep.name, provider),
        onScheduled: (provider, payload) =>
            onTrackHandoffScheduled?.(formStep.name, provider, payload),
        onError: (provider) => onTrackHandoffError?.(formStep.name, provider),
        onFallback: (fallbackStep) => onExternalWidgetFallback?.(fallbackStep),
    });

    // The partner embed shows its OWN loading UI (e.g. CostGuide's #step-loading) while it works,
    // which the hook still reports as "pending". Watch the anchor locally so our placeholder steps
    // aside the moment their content appears -- otherwise two spinners show at once.
    //
    // Deliberately local state rather than something exposed from useExternalWidget: returning
    // anchor state from the hook re-runs its mount effect on a fresh config object, which
    // early-exits via hasMountedRef *after* cleanup has torn down the render-timeout timer, so
    // no_render never fires. The hook's own tests catch that. externalWidgetConfig is a stable
    // reference here, so watching it from this component is safe.
    //
    // ASSUMPTION for the next partner integration: "any child element" is treated as "the partner
    // is showing something". That holds for CostGuide, whose first write is its own visible
    // #step-loading spinner. A partner whose first write is an empty wrapper it populates a moment
    // later would retire our placeholder early and leave a blank gap until their content paints.
    // If that shows up, tighten the predicate here (e.g. require text content or a known step id)
    // rather than removing the check -- without it CostGuide stacks two spinners.
    //
    // useLayoutEffect, NOT useEffect: steps are never unmounted, only shown and hidden via a CSS
    // class (FormDisplay), so re-entering a handoff step whose embed already rendered goes through
    // a real render with the flag freshly reset to false. A passive effect runs after paint, which
    // would flash the placeholder over an embed that is already there for one frame -- the exact
    // jarring pop-in this component exists to remove. Correcting before paint closes that window.
    // jsdom never paints, so no test can catch a regression here; see WidgetStylesheet for the
    // same reasoning.
    const [externalWidgetContentShown, setExternalWidgetContentShown] = useState(false);
    useLayoutEffect(() => {
        if (!externalWidgetConfig) {
            setExternalWidgetContentShown(false);
            return undefined;
        }
        const anchorId = externalWidgetConfig.anchorId;
        const anchor = document.getElementById(anchorId);
        if (!anchor) {
            return undefined;
        }
        if (anchor.childElementCount > 0) {
            setExternalWidgetContentShown(true);
            return undefined;
        }
        const observer = new MutationObserver(() => {
            const el = document.getElementById(anchorId);
            if (el && el.childElementCount > 0) {
                setExternalWidgetContentShown(true);
                // Nothing left to watch, and a booking widget mutates its subtree constantly
                // once it is live -- keeping this attached would invoke the callback for the
                // rest of the step's lifetime for no benefit.
                observer.disconnect();
            }
        });
        observer.observe(anchor, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, [externalWidgetConfig]);

    const checkConditions = useCallback(() => {
        const newConditions = {};

        Object.entries(fieldTests.current).forEach(([name, f]) => {
            try {
                // Create context with null-safe helper functions
                const context = {
                    ...getData(),
                    ...stepData.current,
                    // Helper for safe array/string checking
                    safeIncludes: (value: any, searchElement: any): boolean => {
                        if (Array.isArray(value)) return value.includes(searchElement);
                        if (typeof value === "string") return value === searchElement;
                        return false;
                    },
                };
                // eslint-disable-next-line @typescript-eslint/ban-types
                newConditions[name] = (f as Function).call(context);
            } catch (error) {
                // Log error in development mode for debugging
                if (process.env.NODE_ENV === "development") {
                    console.error(`Condition evaluation failed for field "${name}":`, error);
                }
                // Default to false if condition fails - field will be hidden
                newConditions[name] = false;
            }
        });

        if (!shallowEqualBooleanMap(newConditions, fieldConditions)) {
            setFieldConditions(newConditions);
        }
    }, [fieldConditions, getData]);

    useEffect(() => {
        const groups: { [key: string]: string[] } = {};

        for (const field of formStep.fields) {
            if (isGroupableFieldField(field)) {
                const groupName = field.mandatoryGroup;
                if (groupName) {
                    if (!groups[groupName]) {
                        groups[groupName] = [];
                    }
                    groups[groupName].push(field.name);
                }
            }
        }

        setMandatoryGroups(groups);
    }, [formStep.fields]);

    // Reset prefill tracking when formStep.fields is replaced by a new array reference
    // (e.g. via FORM_STEP_UPDATE). This must happen in the render phase — not useEffect —
    // for the same reason as the seeding loop below (see lines 247–252).
    const resetPrefillTracking = () => {
        prefilledFields.current.clear();
        hasNotifiedPrefill.current = false;
        stepData.current = {};
    };

    if (prevFieldsRef.current !== formStep.fields) {
        prevFieldsRef.current = formStep.fields;
        resetPrefillTracking();
    }

    // Seed prefilled values synchronously during render so they're available
    // as value props when children mount. This must happen in the render phase
    // (not useEffect/useLayoutEffect) because both fire children-first, and child
    // components (e.g. FormFieldTextInput) call onFieldChange on mount with their
    // default value, which would overwrite the prefill. The guards make this idempotent
    // and safe under Strict Mode double-renders.
    for (const field of formStep.fields) {
        if (
            hasFieldValue(field) &&
            !prefilledFields.current.has(field.name) &&
            !Object.prototype.hasOwnProperty.call(stepData.current, field.name)
        ) {
            stepData.current[field.name] = field.value;
            prefilledFields.current.add(field.name);
        }
    }

    useEffect(() => {
        // check for initial data and look for conditions
        for (const field of formStep.fields) {
            if (field.type === "CHIPS") {
                // look for preselected chips
                const chipsSelectedFromProps = (field as FormChipsInput).items
                    .filter((item) => isSelectableItem(item))
                    .filter((item) => item.selected)
                    .map((item) => item.id);

                // if we have length, set it (guard prevents overwriting prefilled or user-entered data)
                if (
                    chipsSelectedFromProps.length > 0 &&
                    !Object.prototype.hasOwnProperty.call(stepData.current, field.name)
                ) {
                    stepData.current[field.name] = chipsSelectedFromProps;
                }
            }

            if (field.condition) {
                const conditionWithThis = addContextPrefix(field.condition);

                // eslint-disable-next-line no-new-func
                fieldTests.current[field.name] = new Function(`return (${conditionWithThis});`);
            }
        }

        // Notify parent of prefilled data once (not on every formStep.fields change)
        if (prefilledFields.current.size > 0 && !hasNotifiedPrefill.current) {
            hasNotifiedPrefill.current = true;
            handleData(stepData.current);
        }

        checkConditions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkConditions, formStep.fields]); // once

    const validate: () => boolean = useCallback(() => {
        const errors: { [name: string]: FieldValidatorResult } = {};
        let isValid = true;
        const corrections: { [name: string]: string } = {};

        // Validate individual fields first
        formStep.fields.forEach((field) => {
            let result: FieldValidatorResult = { valid: true };

            const value = stepData.current[field.name];

            // Skip validation for hidden fields
            if (fieldConditions[field.name] === false) {
                errors[field.name] = result;
                return;
            }

            // For dropdowns, always validate if mandatory (even if not touched)
            if (isFormDropdownInput(field)) {
                // Check if mandatory field has a value selected
                if (
                    field.mandatory &&
                    (value === undefined || value === null || value === "none")
                ) {
                    result = {
                        valid: false,
                        message: field.mandatoryError || "Please select an option",
                    };
                } else if (value !== undefined && value !== null && value !== "none") {
                    // Check if the selected dropdown item requires free-form input
                    const selectedItem = field.items.find((item) => item.id === value);

                    if (selectedItem && (selectedItem as any).freeFormInput) {
                        // Get the free-form field name (default to "message")
                        const freeFormFieldName =
                            (selectedItem as any).freeFormFieldName || "message";
                        const freeFormValue = stepData.current[freeFormFieldName];

                        // Validate that free-form field has a value
                        // If validation fails, set error on the free-form field, not the dropdown
                        if (
                            !freeFormValue ||
                            (typeof freeFormValue === "string" && freeFormValue.trim() === "")
                        ) {
                            // Set error on the free-form field specifically
                            errors[freeFormFieldName] = {
                                valid: false,
                                message: "Please specify additional details",
                            };
                            // Mark the form as invalid
                            result = {
                                valid: false,
                                message: "", // No error on dropdown itself
                            };
                        }
                    }
                }
            }
            // Like dropdowns, a mandatory range is validated whether or not it was touched.
            // FormFieldDateRange only reports through onFieldChange once the user edits it,
            // so an untouched field never lands in stepData and the touched-only branch below
            // would skip it entirely - which is exactly how a mandatory range submitted empty.
            //
            // The inputs' own required/min/max cannot cover this: the step has no <form> and
            // Next is not type="submit", so the browser never runs constraint validation.
            //
            // A half-filled range fails too. An open-ended window is not a date range, and
            // delivering one as a lead is worse than asking the user to finish.
            else if (isFormDateRangeInput(field)) {
                if (field.mandatory && !isCompleteRange(value)) {
                    result = {
                        valid: false,
                        message: field.mandatoryError || "Please select a date range",
                    };
                }
            }
            // Same reasoning as the range above, and the same failure to avoid: a
            // mandatory datetime needs BOTH halves, and a field the user never touched
            // has to fail rather than fall through the touched-only branch below.
            else if (isFormDateTimeInput(field)) {
                if (field.mandatory && !isCompleteDateTime(value)) {
                    result = {
                        valid: false,
                        message: field.mandatoryError || "Please select a date and a time",
                    };
                }
            }
            // Only validate other fields if they've been touched
            else if (Object.prototype.hasOwnProperty.call(stepData.current, field.name)) {
                if (isFormTextInput(field)) {
                    if (field.mandatory === true && isEmptyString(value)) {
                        result = {
                            valid: false,
                            message: field.mandatoryError || "This field is mandatory",
                        };
                    } else if (field.format === "EMAIL" && !isEmptyString(value)) {
                        result = validateEmail(value);
                    } else if (field.format === "PHONE" && !isEmptyString(value)) {
                        result = validatePhoneNumber(value);
                        if (result.valid) {
                            const formattedNumber = formatUSPhoneNumber(value);
                            if (formattedNumber !== value)
                                corrections[field.name] = formattedNumber;
                        }
                    } else if (field.format === "ZIP_CODE" && !isEmptyString(value)) {
                        result = validateZipCode(value);
                        if (result.valid) {
                            const formattedZip = formatZipCode(value);
                            if (formattedZip !== value) corrections[field.name] = formattedZip;
                        }
                    }
                } else if (isFormChipsInput(field)) {
                    if (field.minRequired && value.length < field.minRequired) {
                        result = {
                            valid: false,
                            message: `Please select at least ${field.minRequired} option(s)`,
                        };
                    } else if (field.maxAllowed && value.length > field.maxAllowed) {
                        result = {
                            valid: false,
                            message: `Please select no more than ${field.maxAllowed} option(s)`,
                        };
                    } else if (field.mandatory && value?.length !== 1) {
                        result = { valid: false, message: "Please select one option" };
                    }
                } else if (isFormDateInput(field) && field.mandatory && value == null) {
                    result = { valid: false, message: "Please select a day" };
                } else if (isFormCheckInput(field) && field.mandatory && !value?.length) {
                    // CHECK is a multi-select unless radio, so any selection satisfies mandatory
                    result = {
                        valid: false,
                        message: field.mandatoryError || "This field is mandatory",
                    };
                }
            }

            isValid = isValid && result.valid;
            errors[field.name] = result;
        });

        // Validate mandatoryGroups: only ONE field in the group is needed
        for (const [, fieldNames] of Object.entries(mandatoryGroups)) {
            const hasValue = fieldNames.some((name) => !isEmpty(stepData.current[name]));

            if (!hasValue) {
                // get the mandatoryError off the field
                const field = formStep.fields.find((f) => f.name === fieldNames[0]);
                const fieldError = fieldErrors[fieldNames[0]];
                const errorMessage =
                    fieldError?.message ||
                    field?.mandatoryError ||
                    "At least one of these fields is required";

                // Mark each field in the group as invalid
                fieldNames.forEach((name) => {
                    errors[name] = {
                        valid: false,
                        message: errorMessage,
                    };
                });

                isValid = false;
            }
        }

        // Apply corrections if needed
        if (!isEmpty(corrections)) {
            stepData.current = { ...stepData.current, ...corrections };
        }

        // Only update state if different
        if (!fieldErrorsEqual(fieldErrors, errors)) {
            setFieldErrors(errors);
        }

        return isValid;
    }, [fieldConditions, fieldErrors, formStep.fields, mandatoryGroups]);

    const continueToNext = async () => {
        if (!validate() || isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        try {
            pushData(stepData.current);

            // Track next_step event if tracking callback is provided
            if (onTrackNextStep && currentStepIndex !== undefined && totalSteps !== undefined) {
                const isLastStep = currentStepIndex === totalSteps - 1;
                onTrackNextStep(
                    formStep.name,
                    currentStepIndex,
                    stepData.current as Record<string, unknown>,
                    isLastStep
                );
            }

            if (nextStep) {
                await nextStep(formStep.nextAction === "submit");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const pushData = (data: object) => {
        const augmentedData: { [key: string]: unknown } = { ...data };

        // Now create the equivalent with string representation for templating
        const dataWithLabels: { [name: string]: string } = {};

        // One place that turns collected times into the `<field>_start` / `<field>_end`
        // lead fields, so the chips field and the datetime field cannot drift apart on
        // the shape a CRM receives.
        const applyTimes = (fieldName: string, startTimes: string[], endTimes: string[]) => {
            if (startTimes.length > 0) {
                const joinedStart = startTimes.join(", ");
                augmentedData[`${fieldName}_start`] = joinedStart;
                dataWithLabels[`${fieldName}_start`] = joinedStart;
            }
            if (endTimes.length > 0) {
                const joinedEnd = endTimes.join(", ");
                augmentedData[`${fieldName}_end`] = joinedEnd;
                dataWithLabels[`${fieldName}_end`] = joinedEnd;
            }
        };

        for (const fieldName of Object.keys(data)) {
            const value = data[fieldName];

            const formField = formStep.fields.find((field) => field.name === fieldName);

            if (formField?.type === "CHIPS" && value !== undefined) {
                const valueLabels: string[] = [];
                const startTimes: string[] = [];
                const endTimes: string[] = [];

                (value as string[]).forEach((s) => {
                    const item = (formField as FormChipsInput).items.find((chip) => chip.id === s);
                    if (item) {
                        valueLabels.push(item.label);
                        const timedItem = item as SelectableItem;
                        if (timedItem.startTime) {
                            startTimes.push(timedItem.startTime);
                        }
                        if (timedItem.endTime) {
                            endTimes.push(timedItem.endTime);
                        }
                    }
                });

                dataWithLabels[fieldName] = joinWithAnd(valueLabels);
                applyTimes(fieldName, startTimes, endTimes);
            } else if (formField?.type === "DATETIME" && value !== undefined) {
                // One field, five lead fields: the label under the field's own name -
                // the same key and shape the chips field it replaces emits today - plus
                // the start, the optional end, the date and the combined timestamp.
                const dateTimeField = formField as FormDateTimeInput;
                const selection = value as FormDateTimeValue;

                augmentedData[fieldName] = selection.label;
                dataWithLabels[fieldName] = selection.label;

                applyTimes(
                    fieldName,
                    selection.start ? [selection.start] : [],
                    selection.end ? [selection.end] : []
                );

                // Never hardcoded: a tenant may submit the pair under its own names.
                const dateFieldName = dateTimeField.dateFieldName || DEFAULT_DATE_FIELD_NAME;
                augmentedData[dateFieldName] = selection.date;
                dataWithLabels[dateFieldName] = selection.date;

                const dateTimeFieldName =
                    dateTimeField.dateTimeFieldName || DEFAULT_DATE_TIME_FIELD_NAME;
                augmentedData[dateTimeFieldName] = selection.datetime;
                dataWithLabels[dateTimeFieldName] = selection.datetime;
            } else if (formField?.type === "DROPDOWN" && value !== undefined) {
                // Find the selected item by ID
                const item = (formField as FormDropdownInput).items.find(
                    (item) => item.id === value
                );
                if (item) {
                    // Use label if available, otherwise format the ID (remove _ and capitalize)
                    if (item.label && item.label.trim().length > 0) {
                        dataWithLabels[fieldName] = item.label;
                    } else {
                        // Format ID: "contact_us" -> "Contact Us"
                        dataWithLabels[fieldName] = value
                            .replace(/_/g, " ")
                            .split(" ")
                            .map(
                                (word: string) =>
                                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                            )
                            .join(" ");
                    }
                } else {
                    dataWithLabels[fieldName] = value;
                }
            } else if (formField?.type === "DATE" && value !== undefined) {
                dataWithLabels[fieldName] = reformatDate(value);
            } else {
                dataWithLabels[fieldName] = value;
            }
        }

        handleData(augmentedData);
        handleDataWithLabels(dataWithLabels);
    };

    const goBackToPrevious = () => {
        pushData(stepData.current);

        if (prevStep) {
            prevStep();
        }
    };

    const isNextDisabled = () => {
        // for each of the fields, check...
        // 1. for the required ones, do we have entries and are they are valid
        // 2. for the optional ones, if they have any content, make sure they are value

        for (const key in fieldErrors) {
            // make sure we are only looking at required fields
            if (fieldConditions[key] === false) {
                continue;
            }
            if (!fieldErrors[key].valid) {
                return true;
            }
        }
        return false;
    };

    const onFieldChange = useCallback(
        (name: string, value: string | string[] | object | undefined) => {
            const isFirst = !Object.prototype.hasOwnProperty.call(stepData.current, name);

            // When a field was just prefilled (e.g. after FORM_STEP_UPDATE reset), the child
            // component's stale useEffect may fire onFieldChange with its old value before it
            // syncs to the new value prop. Block stale updates; clear the guard once the child
            // reports the matching prefilled value (or any subsequent call after sync).
            if (prefilledFields.current.has(name)) {
                if (!isFirst && stepData.current[name] === value) {
                    // Child synced to the prefilled value — clear guard, no-op (value unchanged)
                    prefilledFields.current.delete(name);
                    return;
                }
                if (!isFirst && stepData.current[name] !== value) {
                    // Stale update from child's old state — skip to preserve prefill
                    return;
                }
            }

            if (!isFirst && stepData.current[name] === value) {
                return;
            }

            if (Array.isArray(value) && value.length === 0) {
                value = undefined; // treat empty arrays as undefined
            }

            stepData.current = { ...stepData.current, [name]: value };

            // Sync to parent ref for tracking purposes
            if (currentStepDataRef) {
                currentStepDataRef.current = stepData.current as Record<string, unknown>;
            }

            checkConditions();

            if (!isEmpty(fieldErrors)) {
                validate();
            }
        },
        [checkConditions, fieldErrors, validate, currentStepDataRef]
    );

    const config = useContext(SearchWidgetConfigContext);

    const { context } = useSearchState();

    checkConditions();

    const getFields = (): JSX.Element[] => {
        if (!formStep) {
            return [];
        }

        const stepFields = formStep.fields || [];

        const fields = stepFields.map((field, index) => {
            let fieldComponent: JSX.Element | null;
            // The last field of the step gets "done" rather than "next" on the mobile keyboard
            const isLastField = index === stepFields.length - 1;

            switch (field.type) {
                case "TEXT":
                    if ((field as FormFieldTextAddressInput).format === "ADDRESS") {
                        fieldComponent = (
                            <FormFieldTextInputAddress
                                key={field.name}
                                field={field as FormFieldTextAddressInput}
                                onFieldChange={onFieldChange}
                                value={stepData.current[field.name]}
                                errorMessage={fieldErrors[field.name]?.message}
                                required={field.mandatory}
                                googleMapsApiKey={config.googleMapsApiKey}
                                mapsBaseUrl={config.mapsBaseUrl}
                            />
                        );
                    } else {
                        fieldComponent = (
                            <FormFieldTextInput
                                key={field.name}
                                field={field as FormTextInput}
                                onFieldChange={onFieldChange}
                                value={stepData.current[field.name]}
                                errorMessage={fieldErrors[field.name]?.message}
                                required={field.mandatory}
                                isLastField={isLastField}
                            />
                        );
                    }
                    break;
                case "DROPDOWN": {
                    // Compute free-form error message if applicable
                    const dropdownField = field as FormDropdownInput;
                    const currentValue = stepData.current[dropdownField.name];
                    let freeFormError: string | undefined;

                    if (currentValue && currentValue !== "none") {
                        const selectedItem = dropdownField.items.find(
                            (item) => item.id === currentValue
                        );
                        if (selectedItem && (selectedItem as any).freeFormInput) {
                            const freeFormFieldName =
                                (selectedItem as any).freeFormFieldName || "message";
                            freeFormError = fieldErrors[freeFormFieldName]?.message;
                        }
                    }

                    fieldComponent = (
                        <FormFieldDropdown
                            key={field.name}
                            field={dropdownField}
                            onFieldChange={onFieldChange}
                            errorMessage={fieldErrors[field.name]?.message}
                            required={field.mandatory}
                            freeFormErrorMessage={freeFormError}
                        />
                    );
                    break;
                }
                case "CHECK":
                    fieldComponent = (
                        <FormFieldSelect
                            key={field.name}
                            field={field as FormChipsInput}
                            onFieldChange={onFieldChange}
                            errorMessage={fieldErrors[field.name]?.message}
                        />
                    );
                    break;
                case "CHIPS":
                    fieldComponent = (
                        <FormFieldChips
                            key={field.name}
                            field={field as FormChipsInput}
                            onFieldChange={onFieldChange}
                            errorMessage={fieldErrors[field.name]?.message}
                            value={stepData.current[field.name] as string[] | undefined}
                        />
                    );
                    break;
                case "DATE":
                    const busyDays = context?.busyDays;

                    fieldComponent = (
                        <FormFieldDate
                            key={field.name}
                            field={field as FormDateInput}
                            onFieldChange={onFieldChange}
                            errorMessage={fieldErrors[field.name]?.message}
                            busyDays={busyDays}
                            loading={loading}
                        />
                    );
                    break;
                case "DATETIME":
                    fieldComponent = (
                        <FormFieldDateTime
                            key={field.name}
                            field={field as FormDateTimeInput}
                            onFieldChange={onFieldChange}
                            value={stepData.current[field.name] as FormDateTimeValue | undefined}
                            errorMessage={fieldErrors[field.name]?.message}
                            required={field.mandatory}
                        />
                    );
                    break;
                case "DATERANGE":
                    fieldComponent = (
                        <FormFieldDateRange
                            key={field.name}
                            field={field as FormDateRangeInput}
                            onFieldChange={onFieldChange}
                            value={stepData.current[field.name] as FormDateRangeValue | undefined}
                            errorMessage={fieldErrors[field.name]?.message}
                            required={field.mandatory}
                            isLastField={isLastField}
                        />
                    );
                    break;

                case "CARD":
                    fieldComponent = (
                        <FormFieldCard
                            key={field.name}
                            field={replaceValues(field, getDataWithLabels()) as FormCardInput}
                            onFieldChange={onFieldChange}
                        />
                    );
                    break;

                default:
                    fieldComponent = null;
            }

            return (
                <div
                    key={index}
                    className={`${styles.xapp_formfield} ${
                        fieldConditions[field.name] !== false ? styles.active : ""
                    }`}
                >
                    {fieldComponent}
                </div>
            );
        });

        return fields;
    };

    const fullBleed = !!formStep && (formStep as FormStepWithUnloadWarning).fullBleed;

    return (
        <div className={fullBleed ? styles.xapp_step_full_bleed : undefined}>
            {!hideIntro && !!formStep && (formStep.title || formStep.subtitle) && (
                <div className={styles.xapp_formfield_title}>
                    {!!formStep.title && <p>{formStep.title}</p>}
                    {!!formStep.subtitle && (
                        <p className={styles.xapp_formfield_subtitle}>{formStep.subtitle}</p>
                    )}
                </div>
            )}

            {!!formStep && (formStep as FormStepWithUnloadWarning).warnBeforeUnloadMessage && (
                <div
                    style={{
                        backgroundColor: "#fff3cd",
                        color: "#856404",
                        padding: "12px 16px",
                        marginBottom: "16px",
                        borderRadius: "4px",
                        border: "1px solid #ffeaa7",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                    }}
                >
                    <span style={{ fontSize: "20px" }}>⚠️</span>
                    <span>{(formStep as FormStepWithUnloadWarning).warnBeforeUnloadMessage}</span>
                </div>
            )}

            {isFormStepIframe(formStep) && (
                <div className={styles.xapp_step_iframe_wrapper}>
                    <iframe
                        src={formStep.iframe.src}
                        title={formStep.title}
                        width={formStep.iframe.width || "100%"}
                        height={formStep.iframe.height || "100%"}
                        frameBorder="0"
                        allowFullScreen
                    />
                </div>
            )}
            {isFormStepExternalWidget(formStep) && (
                <div className={styles.xapp_external_widget_container}>
                    <div
                        id={formStep.externalWidget.anchorId}
                        className={styles.xapp_external_widget_anchor}
                    />
                    {isActive &&
                        externalWidgetOutcome === "pending" &&
                        !externalWidgetContentShown && (
                            <div
                                className={styles.xapp_external_widget_loading}
                                role="status"
                                aria-live="polite"
                            >
                                <span className={styles.xapp_external_widget_spinner}>
                                    <SpinnerIcon />
                                </span>
                                <p>Finding available appointments&hellip;</p>
                            </div>
                        )}
                    {(externalWidgetOutcome === "no_render" || externalWidgetOutcome === "error") &&
                        !formStep.externalWidget.fallbackStep && (
                            <div className={styles.xapp_external_widget_fallback}>
                                <p>
                                    {
                                        "We're sorry, we couldn't load the booking form. Please try again later."
                                    }
                                </p>
                            </div>
                        )}
                </div>
            )}
            <div>{getFields()}</div>

            {!!submitError && (
                <div className={styles.xapp_submit_error} role="alert">
                    <p>{submitError}</p>
                </div>
            )}

            {/* For final steps, show the next button centered above the previous button */}
            {!!nextStep && formStep.nextAction !== "omit" && (formStep as any).final && (
                <div className={styles.xapp_final_button_container}>
                    <Button
                        className={
                            isNextDisabled() || isSubmitting
                                ? styles.xapp_disabled_button
                                : formStep.nextLabel
                                ? styles.xapp_secondary_button
                                : styles.xapp_primary_button
                        }
                        disabled={isSubmitting}
                        onClick={continueToNext}
                        variant="contained"
                    >
                        {isSubmitting ? "Loading..." : formStep.nextLabel || "Next"}
                    </Button>
                </div>
            )}

            <div className={styles.xapp_form_nav_buttons}>
                {prevStep === undefined && <div className={styles.xapp_dummy_button}></div>}

                {!!prevStep && formStep.previousAction !== "omit" && (
                    <Button
                        className={styles.xapp_primary_button}
                        onClick={goBackToPrevious}
                        // color="primary"
                        variant="contained"
                    >
                        {formStep.previousLabel || "Previous"}
                    </Button>
                )}

                {/* For non-final steps, show the next button in the normal position */}
                {!!nextStep && formStep.nextAction !== "omit" && !(formStep as any).final && (
                    <Button
                        className={
                            isNextDisabled() || isSubmitting
                                ? styles.xapp_disabled_button
                                : formStep.nextLabel
                                ? styles.xapp_secondary_button
                                : styles.xapp_primary_button
                        }
                        disabled={isSubmitting}
                        onClick={continueToNext}
                        // color={formStep.nextLabel ? "secondary" : "primary"}
                        variant="contained"
                    >
                        {isSubmitting ? "Loading..." : formStep.nextLabel || "Next"}
                    </Button>
                )}
            </div>
        </div>
    );
};

export default FormStepDisplay;
